import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { getStoredUser } from './user-storage.util';

/**
 * Guards the internal Management area (`/internal/manage`) behind the
 * per-user `canManage` flag, on top of the INTERNAL role already enforced by
 * {@link RoleGuard}.
 *
 * `canManage` is DB-authoritative (no JWT claim) — it rides the stored `User`
 * object populated at login, same as `role`. Mirrors RoleGuard's shape: a
 * class-based `CanActivate` returning a `UrlTree` redirect rather than
 * navigating imperatively.
 *
 * Read-only managers (`canManageReadOnly`) may also enter — they see the whole
 * console but no write controls (the FE hides them; connect-service enforces
 * the write block server-side). `canManage` (full) wins if both flags are set.
 */
@Injectable({
  providedIn: 'root',
})
export class ManageGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    const user = getStoredUser();

    // A missing or empty role defaults to PROFESSIONAL_ASSOCIATE, same as RoleGuard.
    const userRole: string = user.role || 'PROFESSIONAL_ASSOCIATE';

    if (userRole !== 'INTERNAL') {
      // Not an internal user at all — send to the PA default, same as RoleGuard.
      return this.router.createUrlTree(['/invitations']);
    }

    if (user.canManage === true || user.canManageReadOnly === true) {
      return true;
    }

    // INTERNAL but neither a full nor a read-only manager — redirect to the audit dashboard.
    return this.router.createUrlTree(['/internal/dashboard']);
  }
}
